import { Accordion, Alert, Badge, Button, Group, Stack, Text } from '@mantine/core';
import { HiOutlinePlus, HiOutlineSquare2Stack } from 'react-icons/hi2';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';
import { LocalSubflowCallStep } from '@/types/automationSteps.ts';

const newId = () => `local_${crypto.randomUUID().replaceAll('-', '').slice(0, 12)}`;

const callerCount = (subflowId: string, steps: LocalSubflowCallStep[] | undefined) =>
  (steps ?? []).filter((step) => step.step_type === 'local_subflow_call' && step.subflow_id === subflowId).length;

const pluralize = (count: number, singular: string) => `${count} ${count === 1 ? singular : `${singular}s`}`;

/** Root-editor inventory for version-local modules; intentionally collapsed by default. */
const LocalModulesAccordion = () => {
  const { data: version } = useRetrieveLatestAutomationVersion();
  const { mutate: applyOperations, isPending, validationFeedback } = useCompositionOperations();
  const { setActiveCompositionTarget } = useActiveCompositionTarget();
  const subflows = version?.local_subflows ?? [];

  const createModule = () => {
    const subflowId = newId();
    applyOperations(
      [{ op_type: 'create_local_subflow', subflow_id: subflowId, subflow_name: 'New module' }],
      {
        onSuccess: () => void setActiveCompositionTarget({
          scope_kind: 'local_subflow',
          subflow_id: subflowId,
        }),
      },
    );
  };

  return (
    <Accordion variant="separated" w="100%" radius="md">
      <Accordion.Item value="modules">
        <Accordion.Control icon={<HiOutlineSquare2Stack size={18} />}>
          <Group gap="xs">
            <Text fw={600}>Modules</Text>
            <Badge variant="light" color="gray">{subflows.length}</Badge>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Private building blocks for this automation. Review and make focused changes here.
            </Text>
            {validationFeedback && (
              <Alert color="red" title="Module change needs attention">
                <Stack gap={2}>
                  {validationFeedback.messages.map((message) => <Text key={message} size="sm">{message}</Text>)}
                </Stack>
              </Alert>
            )}
            {subflows.map((subflow) => (
              <Stack key={subflow.subflow_id} gap={2} p="xs" style={{ borderRadius: 8 }}>
                <Group justify="space-between" align="flex-start">
                  <Stack gap={0}>
                    <Text fw={600} size="sm">{subflow.name}</Text>
                    <Text size="xs" c="dimmed">
                      {subflow.description || 'Private version-local module'}
                    </Text>
                  </Stack>
                  <Group gap={4}>
                    <Badge variant="light" color={subflow.steps.length === 0 ? 'yellow' : 'gray'}>
                      {subflow.steps.length === 0 ? 'Needs steps' : pluralize(subflow.steps.length, 'step')}
                    </Badge>
                    <Badge variant="light" color="gray">
                      {pluralize(
                        callerCount(subflow.subflow_id, version?.steps as LocalSubflowCallStep[] | undefined),
                        'call',
                      )}
                    </Badge>
                  </Group>
                </Group>
                <Group gap="xs">
                  <Button
                    aria-label={`Open module ${subflow.name}`}
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={() => void setActiveCompositionTarget({
                      scope_kind: 'local_subflow',
                      subflow_id: subflow.subflow_id,
                    })}
                  >
                    Open module
                  </Button>
                  <Button
                    aria-label={`Insert ${subflow.name} into workflow`}
                    size="xs"
                    variant="subtle"
                    onClick={() => void setActiveCompositionTarget({
                      scope_kind: 'local_subflow',
                      subflow_id: subflow.subflow_id,
                      intent: 'insert_call',
                    })}
                  >
                    Insert into workflow
                  </Button>
                </Group>
              </Stack>
            ))}
            <Button
              variant="filled"
              color="brandBlue.7"
              c="white"
              styles={{
                label: { color: 'white' },
                section: { color: 'white' },
              }}
              leftSection={<HiOutlinePlus size={16} />}
              loading={isPending}
              onClick={createModule}
            >
              Create module
            </Button>
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default LocalModulesAccordion;
